import { Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import Icon from "../components/Icons";
import CtaBand from "../components/CtaBand";
import ServiceArt from "../components/ServiceArt";
import styles from "./FitnessPage.module.css";
import { categories, servicesByCategory } from "../data/services";
import { training } from "../data/content";

/**
 * Fitness gets its own layout rather than the shared CategoryPage
 * grid: it is the one branch that is genuinely a *sequence* — phases
 * you progress through — so it is presented as an alternating
 * editorial rundown with a phase rail, not a bag of equal cards.
 */
export default function FitnessPage() {
  const items = servicesByCategory("fitness");
  const category = categories.fitness;

  /* Phase labels come from content.js where they exist, matched by
     position — the two lists describe the same programme. */
  const phaseFor = (i) => training.items[i]?.phase;

  return (
    <>
      <PageHeader
        eyebrow={category.label}
        title={category.title}
        lead={category.lead}
      />

      {/* --- The premise ------------------------------------------- */}
      <section className={`u-section ${styles.premise}`}>
        <div className={`u-shell ${styles.premiseInner}`}>
          <Reveal className={styles.premiseCopy}>
            <p className={styles.premiseKicker}>Why this exists</p>
            <p className={styles.premiseText}>
              Most re-injuries happen after the pain has gone. The symptom
              settles, treatment stops, and the tissue never regains the
              capacity it lost — so the same thing happens again three months
              later. This is the part that prevents that.
            </p>
          </Reveal>

          <Reveal className={styles.premiseArt} delay={90}>
            <ServiceArt art="progression" />
          </Reveal>
        </div>
      </section>

      {/* --- Phase rundown ------------------------------------------ */}
      <section className={`u-section ${styles.programmes}`}>
        <div className="u-shell">
          <ol className={styles.list}>
            {items.map((item, i) => (
              <Reveal
                as="li"
                key={item.slug}
                id={item.slug}
                delay={Math.min(i, 4) * 70}
                className={styles.row}
              >
                <div className={styles.rail} aria-hidden="true">
                  <span className={styles.num}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className={styles.railLine} />
                </div>

                <div className={styles.rowBody}>
                  <div className={styles.rowHead}>
                    <span className={styles.rowIcon}>
                      <Icon name={item.icon} size={24} />
                    </span>
                    <div>
                      {phaseFor(i) && (
                        <span className={styles.phase}>{phaseFor(i)}</span>
                      )}
                      <h2 className={styles.rowTitle}>{item.title}</h2>
                    </div>
                  </div>

                  <p className={styles.rowShort}>{item.short}</p>
                  <p className={styles.rowIntro}>{item.intro}</p>

                  {item.helps?.length > 0 && (
                    <ul className={styles.tags}>
                      {item.helps.map((h) => (
                        <li key={h}>{h}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* --- Progression criteria ----------------------------------- */}
      <section className={`u-section ${styles.criteria}`}>
        <div className="u-shell">
          <Reveal>
            <h2 className={styles.criteriaTitle}>
              You progress on measurements, not on dates.
            </h2>
            <p className={styles.criteriaLead}>
              Every stage has an exit condition. Nothing advances because a
              number of weeks has passed.
            </p>
          </Reveal>

          <div className={styles.criteriaGrid}>
            {[
              {
                icon: "joint",
                label: "Range",
                text: "Full, pain-free movement at the joint before it is loaded.",
              },
              {
                icon: "strength",
                label: "Strength symmetry",
                text: "The injured side within a set percentage of the other.",
              },
              {
                icon: "taichi",
                label: "Control under fatigue",
                text: "Movement quality holds at the end of a session, not just the start.",
              },
              {
                icon: "hiit",
                label: "Sport-specific load",
                text: "The actual demand of your sport, reproduced and tolerated.",
              },
            ].map((c, i) => (
              <Reveal key={c.label} delay={i * 70} className={styles.criterion}>
                <span className={styles.criterionIcon}>
                  <Icon name={c.icon} size={20} />
                </span>
                <h3 className={styles.criterionLabel}>{c.label}</h3>
                <p className={styles.criterionText}>{c.text}</p>
              </Reveal>
            ))}
          </div>

          <Reveal className={styles.note}>
            <p>
              Not sure which phase you are in? That is what the assessment
              establishes.{" "}
              <Link to="/contact">Book one</Link> and you will leave knowing.
            </p>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
